import {
  FabricEditor,
  FabricEditorCanvas,
  FabricEditorProvider,
  gridPlugin,
  layerPlugin,
  selectionPlugin,
} from 'modules/fabric-editor';
import { layoutBlocksPlugin } from 'modules/manager/domain/layout-blocks';
import { placeholderPlugin } from 'modules/manager/domain/placeholder';
import { applyLayoutBackgrounds, isRenderImageError, substitutePlaceholders } from 'modules/manager/domain/render';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { fetchBoardOnly, resetEditor, selectActiveBoard, selectError } from 'modules/manager/store';
import { useGetRenderContextQuery } from 'modules/manager/store/render-api';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const RENDER_PLUGINS = [
  selectionPlugin(),
  layerPlugin(),
  gridPlugin({ visible: false }),
  placeholderPlugin(),
  layoutBlocksPlugin(),
];

const BoardRenderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const matchId = parseId(params.get('matchId'));
  const boardId = parseId(params.get('boardId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeBoard = useAppSelector(selectActiveBoard);
  const loadError = useAppSelector(selectError);
  const ctxQuery = useGetRenderContextQuery(matchId !== null ? { matchId } : skipToken);

  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [status, setStatus] = useState<'loading' | 'rendering' | 'done' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const orchestrated = useRef(false);

  useEffect(() => {
    if (boardId === null) {
      setStatus('error');
      setErrorMessage('Missing boardId in URL');
      return;
    }
    dispatch(fetchBoardOnly({ boardId }));
    return () => {
      dispatch(resetEditor());
    };
  }, [dispatch, boardId]);

  useEffect(() => {
    if (matchId === null) {
      setStatus('error');
      setErrorMessage('Missing matchId in URL');
    }
  }, [matchId]);

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeBoard || !ctxQuery.data) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        await loadCanvasJson(editor, activeBoard.version, activeBoard.objects ?? []);
        await applyLayoutBackgrounds(editor, bgUrls);
        await substitutePlaceholders(editor, ctxQuery.data!);
        editor.canvas.requestRenderAll();
        await nextFrame();
        const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });
        await mediaManagerRepository.persistRenderedImage({
          matchId: matchId!,
          boardId: activeBoard.id,
          dataUrl,
        });
        setStatus('done');
      } catch (err) {
        console.error(err);
        const userMessage = isRenderImageError(err)
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
        setStatus('error');
        setErrorMessage(userMessage);
      }
    })();
  }, [editor, activeBoard, ctxQuery.data, loadError, bgUrls]);

  useEffect(() => {
    if (ctxQuery.error) {
      console.error(ctxQuery.error);
      setStatus('error');
      setErrorMessage((ctxQuery.error as { message?: string }).message ?? 'Failed to load render context');
    }
  }, [ctxQuery.error]);

  if (!activeBoard) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading board…'} />;
  }

  return (
    <>
      <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
        <FabricEditorProvider editor={editor}>
          <FabricEditorCanvas
            key={`${activeBoard.id}-${activeBoard.width}x${activeBoard.height}`}
            width={activeBoard.width}
            height={activeBoard.height}
            resolution={{ width: activeBoard.width, height: activeBoard.height }}
            plugins={RENDER_PLUGINS}
            onReady={setEditor}
          />
        </FabricEditorProvider>
      </div>
      <StatusOverlay status={status} message={errorMessage} />
    </>
  );
};

const StatusOverlay: React.FC<{ status: string; message: string | null }> = ({ status, message }) => (
  <div className="flex items-center justify-center w-screen h-screen text-gray-600">
    <div className="text-center">
      <div className="text-xl font-bold mb-2">{statusTitle(status)}</div>
      {message && <div className="text-sm">{message}</div>}
    </div>
  </div>
);

function statusTitle(status: string): string {
  switch (status) {
    case 'loading': return 'Loading board and match data…';
    case 'rendering': return 'Rendering PNG…';
    case 'done': return 'Image saved successfully.';
    case 'error': return 'Render failed';
    default: return status;
  }
}

function parseId(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function collectBgUrls(params: URLSearchParams): string[] {
  const out: string[] = [];
  for (let i = 1; i <= 50; i++) {
    const value = params.get(`bg${i}`);
    if (value === null) {
      if (i === 1) continue;
      break;
    }
    out.push(value);
  }
  return out;
}

function loadCanvasJson(editor: FabricEditor, version: unknown, objects: unknown[]): Promise<void> {
  return new Promise((resolve) => {
    editor.canvas.loadFromJSON({ version, objects }, () => {
      editor.canvas.renderAll();
      editor.emit('canvas:loaded', { source: 'render' });
      resolve();
    });
  });
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
}

export default BoardRenderPage;
